import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import api from '../utils/api.util';
import toast from '../utils/toast.util';
import { useAuth } from '../auth/AuthContext';
import { ENDPOINT } from '../constants/common-app.const';
import { GroupState, TrainingGroup } from '../pages/CourseEnrollment/CourseEnrollment.const';

const transformGroup = (g: any, idx: number): TrainingGroup => {
  const now = dayjs();
  const start = dayjs(g.startDate);
  const end = dayjs(g.endDate);
  const isActive = (now.isAfter(start) || now.isSame(start, 'day')) && (now.isBefore(end) || now.isSame(end, 'day'));

  const state: GroupState = isActive ? 'open' : g.status === 'open' ? 'open' : g.status === 'forming' ? 'forming' : 'waiting';

  return {
    id: g.id,
    name:
      idx === 0 && (isActive || g.status === 'open' || g.status === 'forming')
        ? 'Текущая группа'
        : `Группа ${g.sequenceNo}/${g.year}`,
    startDate: start.format('DD.MM.YYYY'),
    endDate: end.format('DD.MM.YYYY'),
    state,
  };
};

const useGetCourseGroups = ({ courseId }: { courseId: string }) => {
  const [groups, setGroups] = useState<TrainingGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`${ENDPOINT.COURSES}/${courseId}/groups`);
      const raw = response ?? [];
      // Payment deadline = the day preceding the next group's start date.
      // Groups arrive ordered (current group first), so the next group is raw[i + 1].
      const mapped = raw.map((g: any, i: number) => {
        const group = transformGroup(g, i);
        const next = raw[i + 1];
        group.paymentDeadline = next
          ? dayjs(next.startDate).subtract(1, 'day').format('DD.MM.YYYY')
          : group.startDate;
        return group;
      });
      setGroups(mapped);
      setLoading(false);
    } catch (err) {
      toast.showOnFailure('Ошибка загрузки групп курса');
      setError('fetch failed');
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    if (isAuthenticated && courseId) {
      fetchGroups();
    }
  }, [isAuthenticated, courseId, fetchGroups]);

  return { groups, loading, error };
};

export default useGetCourseGroups;
